<template>
  <div ref="toolbarRef" class="toolbar">
    <aside :class="{ open }">
      <menu ref="menu" class="toolbar-menu">
        <slot />
      </menu>
      <div class="toolbar-head">
        <h3 class="whitespace-nowrap inline-block">
          {{ selected?.title }}
        </h3>
        <button class="flex justify-center items-center w-5 h-5" @click="close">
          <close-icon class="w-3 h-3 text-black-total stroke-2" />
        </button>
      </div>
      <section id="toolbar-pane" class="h-full" />
    </aside>
  </div>
</template>

<script lang="ts" setup>
import {
  computed, nextTick, provide, ref, unref, reactive,
} from 'vue';
import CloseIcon from '@images/close.svg?component';
import { useElementBounding } from '@vueuse/core';
import { toolbarKey, type Pane } from './injects';

const selected = ref<Pane | null>(null);
const open = ref(false);
const menu = ref(null);
const toolbarRef = ref(null);

const { top } = useElementBounding(toolbarRef);
const clampedTop = computed(() => Math.max(0, unref(top)));

const close = async () => {
  await nextTick();
  open.value = false;
  selected.value = null;
};

const select = async (pane: Pane) => {
  selected.value = pane;
  await nextTick();
  open.value = true;
};

const toolbar = reactive({
  open,
  select,
  selected: computed(() => unref(selected)?.id),
  close,
});

provide(toolbarKey, toolbar);
</script>

<style lang="scss" scoped>
.toolbar {
  @apply sticky top-0 right-0;

  --menu-width: var(--toolbar-menu-width);
  --toolbar-top: v-bind('`${clampedTop}px`');

  --toolbar-padding-x: #{px(40)};
  --toolbar-padding-y: #{px(34)};

  width: var(--menu-width);
  height: min(100%, calc(100vh - var(--toolbar-top)));
  z-index: 100;

  grid-area: toolbar;
}
.toolbar-menu {
  @apply border-r border-r-element-neutral;
  @apply text-center;
  min-width: var(--menu-width);

  grid-column: 1;
  grid-row: 1 / 3;
  grid-area: menu;
}
.toolbar-head {
  @apply border-b border-b-element-neutral;
  @apply flex justify-between gap-x-8;
  @apply w-full py-7.5 px-10;
  grid-area: title;
}

#toolbar-pane {
  @apply h-full relative w-max overflow-y-auto overscroll-y-contain;
  padding: var(--toolbar-padding-y) var(--toolbar-padding-x);
  grid-area: content;
}

aside {
  @apply grid;
  @apply absolute left-0 h-full w-fit z-toolbar transition-transform;
  @apply bg-background-light;
  @apply transition-transform;
  @apply border-t border-t-element-neutral;
  @apply border-l border-l-element-neutral;

  grid-template-columns: auto max-content;
  grid-template-rows: auto 1fr;

  grid-template-areas:
    "menu title"
    "menu content";

  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

  &.open {
    transform: translateX(calc((100% - var(--menu-width)) * -1));
  }
}
</style>
